/* Created by Interactive 63 - https://interactive63.com */
/**
 * Pure helpers for the easing curve graph: mapping between graph pixels and easing values,
 * clamping to the visible range, snapping, and reading control points out of an easing.
 */
import { KEYWORD_BEZIERS } from '../../../core/easing/cubic-bezier';
import type { Easing } from '../../../core/model/types';

export const GRAPH_SIZE = 220;
export const Y_MIN = -0.5;
export const Y_MAX = 1.5;
export const SNAP_STEP = 0.05;

/** Limits a number to an inclusive range. */
function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Rounds to a fixed number of decimal places. */
function round(value: number, places: number): number {
  const factor = 10 ** places;
  return Math.round(value * factor) / factor;
}

/** Maps easing coordinates (time 0..1, progress -0.5..1.5) to graph pixels. */
export function toGraph(x: number, y: number, size = GRAPH_SIZE): { px: number; py: number } {
  return { px: x * size, py: ((Y_MAX - y) / (Y_MAX - Y_MIN)) * size };
}

/** Maps graph pixels back to easing coordinates, clamped to the visible range. */
export function fromGraph(px: number, py: number, size = GRAPH_SIZE): { x: number; y: number } {
  return {
    x: clamp(px / size, 0, 1),
    y: clamp(Y_MAX - (py / size) * (Y_MAX - Y_MIN), Y_MIN, Y_MAX),
  };
}

/** Snaps a value to the nearest step. */
export function snapToStep(value: number, step = SNAP_STEP): number {
  return round(Math.round(value / step) * step, 4);
}

/** Clamps and rounds a control point as it is dragged. Shift snaps to the step. */
export function dragControlPoint(
  px: number,
  py: number,
  snap: boolean,
  size = GRAPH_SIZE,
): { x: number; y: number } {
  const point = fromGraph(px, py, size);
  const x = snap ? snapToStep(point.x) : point.x;
  const y = snap ? snapToStep(point.y) : point.y;
  return { x: round(clamp(x, 0, 1), 2), y: round(clamp(y, Y_MIN, Y_MAX), 2) };
}

/** The four control values of curve easings, or null for easings without handles. */
export function controlPointsOf(easing: Easing): [number, number, number, number] | null {
  switch (easing.type) {
    case 'linear':
      return [0, 0, 1, 1];
    case 'keyword':
      return [...KEYWORD_BEZIERS[easing.name]];
    case 'cubic':
      return [easing.x1, easing.y1, easing.x2, easing.y2];
    default:
      return null;
  }
}

/** Whether two easings are identical. */
export function sameEasing(a: Easing | null, b: Easing | null): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}
